/* eslint-disable no-unused-vars */
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PackageDetailsBasicInformationTab from '../components/package_details/PackageDetailBasicInformationTab';
import zoeShipmentTrackingService from '../services/zoe-shipment-tracking-service';

const formatDateTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', // EST
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short' // "EST"
  }).format(date);
};

export default function ZoeShipmentTrackingDetailsPage() {
  const [shipmentDetails, setShipmentDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);

  // Modal state
  const [openEditModal, setOpenEditModal] = useState(false);
  const [formValues, setFormValues] = useState({});

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenEdit = () => {
    setFormValues(shipmentDetails); // preload current values
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setOpenEditModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await zoeShipmentTrackingService.updateShipmentTracking(
        id,
        formValues
      );
      if (response.success) {
        setShipmentDetails(formValues);
        setOpenEditModal(false);
      } else {
        console.error('Update error:', response.message);
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  useEffect(() => {
    const fetchShipmentTrackingDetails = async () => {
      try {
        const response =
          await zoeShipmentTrackingService.getShipmentTracking(id);
        if (response.success) {
          setShipmentDetails(response.data);
        } else {
          console.error('Error:', response.message);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShipmentTrackingDetails();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  const overviewItems = [
    { title: 'ID', value: shipmentDetails.ZoeShipmentTrackingID },
    {
      title: 'ShipmentDate',
      value: formatDateTime(shipmentDetails.ShipmentDate)
    },
    { title: 'Tracking Number', value: shipmentDetails.TrackingNumber },
    { title: 'Source', value: shipmentDetails.Source },
    { title: 'ServiceName', value: shipmentDetails.ServiceName },
    { title: 'MessageId', value: shipmentDetails.MessageId },
    { title: 'CarrierName', value: shipmentDetails.CarrierName },
    { title: 'OrderNumber', value: shipmentDetails.OrderNumber },
    { title: 'Username', value: shipmentDetails.Username },
    { title: 'BatchId', value: shipmentDetails.BatchId },
    { title: 'ReferenceId', value: shipmentDetails.ReferenceId },
    {
      title: 'CreatedDate',
      value: formatDateTime(shipmentDetails.CreatedDate)
    },
    { title: 'ZoeOrderId', value: shipmentDetails.ZoeOrderId },
    { title: 'Status', value: shipmentDetails.Status },
    {
      title: 'LastUpdated',
      value: formatDateTime(shipmentDetails.LastUpdated)
    },
    { title: 'Note', value: shipmentDetails.Note },
    { title: 'ShipToAddress', value: shipmentDetails.ShipToAddress },
    { title: 'ShipFromAddress', value: shipmentDetails.ShipFromAddress },
    { title: 'TotalQuantity', value: shipmentDetails.TotalQuantity }
  ];

  return (
    <div>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: '#f8f9fa',
          minHeight: '100vh'
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Zoe Shipment Tracking Details
        </Typography>

        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="fullWidth"
              sx={{ backgroundColor: 'white', flex: 1 }}
            >
              <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
            </Tabs>

            {/* Edit Button */}
            {activeTab === 0 && (
              <IconButton
                onClick={handleOpenEdit}
                sx={{ mr: 2 }}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {activeTab === 0 && (
              <PackageDetailsBasicInformationTab
                id={id}
                overviewItems={overviewItems}
                collapsibleItems={[]}
              />
            )}
          </Box>
        </Paper>
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Shipment Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.keys(formValues).map((key) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={key}
                  name={key}
                  value={formValues[key] ?? ''}
                  onChange={handleInputChange}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
